import {
  CodeModuleLoaderType,
  isLoopbackBundleUrl,
  type FederationLoaderConfig,
} from '@packages/systems/code-modules/core';
import type {CodeIslandSize, CodeIslandState} from '../CodeIslandSharedUtils';
import type {ComponentStateScenario} from '@packages/systems/code-libraries/component-states';

type WindowWithStore = Window & {
  CodeIslandCustomElementStore?: CodeIslandCustomElementStore;
};

type Listener = () => void;

export type LocalDevOverrideTarget = FederationLoaderConfig['val'];

function omitError<T extends {error?: Error}>(obj: T): Omit<T, 'error'> {
  const {error: _, ...rest} = obj;
  return rest;
}

class CodeIslandCustomElementStore {
  public listeners: Listener[] = [];
  public state: Record<string, CodeIslandState> = {};
  public refreshCounters: Record<string, number> = {};
  public lastKnownSizes: Record<string, CodeIslandSize> = {};
  /** Connected instances by loader type, so `remountAll` only targets islands that exist right now. */
  private mountedLoaderTypes = new Map<string, CodeModuleLoaderType>();
  /**
   * A real component's `{moduleId}/{submoduleId}` to the local dev server's
   * bundle that renders in its place. Client-only; see `localDevOverrideRegistry.ts`.
   */
  private localDevOverrides = new Map<string, LocalDevOverrideTarget>();
  /**
   * Per-instance scenario binding for multi-state preview grid frames
   * Maps each grid frame's full designer instance id to the scenario that
   * frame represents.
   */
  public scenarioBindings: Record<string, ComponentStateScenario> = {};

  /**
   * If running inside an iframe (e.g. default designer mode), returns the parent window
   * Otherwise (e.g. custom code preview, published sites), returns main window
   *
   * @returns {WindowWithStore} The window the store is attached to
   */
  static getWindow(): WindowWithStore {
    return window.frameElement ? window.parent : window;
  }

  /**
   * Initialize the store and attach it to the window so it can be shared as a singleton
   *
   * @returns {CodeIslandCustomElementStore} The store instance
   */
  static init() {
    const store = new CodeIslandCustomElementStore();
    const win = CodeIslandCustomElementStore.getWindow();
    win.CodeIslandCustomElementStore = store;
    return store;
  }

  /**
   * Get the singleton store instance
   *
   * @returns {CodeIslandCustomElementStore} The store instance
   */
  static getInstance() {
    return CodeIslandCustomElementStore.getWindow()
      .CodeIslandCustomElementStore;
  }

  /**
   * Subscribe to the store
   *
   * @param {Listener} listener - The listener to subscribe
   *
   * @returns {Function} The unsubscribe function
   */
  public subscribe(listener: Listener) {
    this.listeners = [...this.listeners, listener];
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Update the store. Merges with existing state and strips error when status !== 'error'.
   *
   * @param {string} id - The element id
   * @param {CodeIslandState} state - The state of the code island
   */
  public update(id: string, state: CodeIslandState) {
    const merged = {...this.state[id], ...state};
    const cleaned =
      merged.status !== 'error'
        ? omitError(merged as typeof merged & {error?: Error})
        : merged;

    this.state = {
      ...this.state,
      [id]: cleaned as CodeIslandState,
    };

    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Get the element state
   *
   * @param {string} id - The element id
   *
   * @returns {CodeIslandState} The state of the code island
   */
  public get(id: string) {
    return this.state[id];
  }

  /**
   * Get the refresh counter for an element
   *
   * @param {string} id - The element id
   *
   * @returns {number} The refresh counter
   */
  public getRefreshCounter(id: string): number {
    return this.refreshCounters[id] || 0;
  }

  /**
   * Increment the refresh counter for an element to force a remount
   *
   * @param {string} id - The element id
   */
  public incrementRefreshCounter(id: string) {
    this.refreshCounters = {
      ...this.refreshCounters,
      [id]: (this.refreshCounters[id] || 0) + 1,
    };

    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Intentionally does NOT notify listeners — sizes are only read when a
   * status change (which notifies) re-renders a loading card.
   */
  public setLastKnownSize(id: string, size: CodeIslandSize) {
    this.lastKnownSizes = {
      ...this.lastKnownSizes,
      [id]: size,
    };
  }

  public getLastKnownSize(id: string): CodeIslandSize | undefined {
    return this.lastKnownSizes[id];
  }

  public hasErroredIslands(options: {
    loaderType: CodeModuleLoaderType;
  }): boolean {
    return Object.keys(this.state).some(
      (id) =>
        this.state[id]?.loaderType === options.loaderType &&
        this.state[id]?.status === 'error'
    );
  }

  public trackMountedInstance(id: string, loaderType: CodeModuleLoaderType) {
    this.mountedLoaderTypes.set(id, loaderType);
  }

  public untrackMountedInstance(id: string) {
    this.mountedLoaderTypes.delete(id);
  }

  /**
   * Does not notify listeners: a registry change reaches the page through
   * `remountAll`, which remounts the islands that read it.
   */
  public setLocalDevOverride(
    moduleId: string,
    submoduleId: string,
    target: LocalDevOverrideTarget
  ) {
    this.localDevOverrides.set(`${moduleId}/${submoduleId}`, target);
  }

  public getLocalDevOverride(
    moduleId: string,
    submoduleId: string
  ): LocalDevOverrideTarget | null {
    const target = this.localDevOverrides.get(`${moduleId}/${submoduleId}`);
    // An override only ever points at the developer's own machine, so a target
    // on any other host is ignored.
    return target && isLoopbackBundleUrl(target.clientModuleUrl)
      ? target
      : null;
  }

  public clearLocalDevOverrides() {
    this.localDevOverrides.clear();
  }

  /**
   * Force-remounts every currently-connected instance of a loader type,
   * regardless of its current status — unlike `forceReloadAll`, which only
   * affects instances already tracked in `state` for error recovery. An island
   * showing the error card has no connected `<code-island>`, so it is not
   * reached; `forceReloadAll` recovers those.
   */
  public remountAll(options: {loaderType: CodeModuleLoaderType}) {
    const ids = [...this.mountedLoaderTypes]
      .filter(([, loaderType]) => loaderType === options.loaderType)
      .map(([id]) => id);
    if (ids.length === 0) return;

    const refreshCounters = {...this.refreshCounters};
    for (const id of ids) {
      refreshCounters[id] = (refreshCounters[id] || 0) + 1;
    }
    this.refreshCounters = refreshCounters;

    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Set matching code-island elements to loading state and increment refresh counters.
   * Used so CodeIsland renders the code-island element (instead of error card) when recovering from error.
   *
   * @param {Object} options - Required options
   * @param {'SITE' | 'FEDERATION'} options.loaderType - Only affect instances with this loader type
   */
  public forceReloadAll(options: {
    loaderType: CodeModuleLoaderType;
    status?: CodeIslandState['status'];
  }) {
    const ids = Object.keys(this.state).filter(
      (id) =>
        this.state[id]?.loaderType === options.loaderType &&
        (!options.status || this.state[id]?.status === options.status)
    );
    if (ids.length === 0) return;

    const loadingState: CodeIslandState = {
      status: 'loading',
      loaderType: options.loaderType,
    };
    this.state = {
      ...this.state,
      ...Object.fromEntries(ids.map((id) => [id, loadingState])),
    };

    this.refreshCounters = ids.reduce(
      (acc, id) => ({
        ...acc,
        [id]: (this.refreshCounters[id] || 0) + 1,
      }),
      {...this.refreshCounters}
    );

    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Returns the scenario binding for a given designer instance id, or
   * `null` when the instance is not bound to a scenario (the default
   * variant-frame case).
   */
  public getScenarioBinding(
    designerInstanceId: string
  ): ComponentStateScenario | null {
    return this.scenarioBindings[designerInstanceId] ?? null;
  }

  /**
   * Replaces the entire per-instance scenario binding map. Notifies
   * subscribers when the resulting map differs from the previous one so
   * the React-side `useCodeIslandScenarioBinding` hook re-renders only
   * the islands whose bindings actually changed.
   *
   * Idempotent: a no-op when the same `(id, label, props)` triples were
   * already stored — callers can dispatch on every state tick without
   * spamming listeners. Props are compared so that editing a scenario's
   * seeded values in place (same id) still propagates to the preview.
   */
  public setScenarioBindings(next: Record<string, ComponentStateScenario>) {
    if (areBindingsEqual(this.scenarioBindings, next)) return;
    this.scenarioBindings = next;
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Set matching code-island elements to error state. Used when compilation errors
   * arrive after initial load (e.g. user edits code and recompiles).
   *
   * @param {Error} error - The compilation error
   * @param {Object} options - Required options
   * @param {'SITE' | 'FEDERATION'} options.loaderType - Only affect instances with this loader type
   */
  public setGlobalError(
    error: Error,
    options: {loaderType: CodeModuleLoaderType}
  ) {
    const ids = Object.keys(this.state).filter(
      (id) => this.state[id]?.loaderType === options.loaderType
    );

    if (ids.length === 0) return;

    this.state = {
      ...this.state,
      ...Object.fromEntries(
        ids.map((id) => [
          id,
          {
            ...this.state[id],
            status: 'error' as const,
            error,
          } as CodeIslandState,
        ])
      ),
    };

    for (const listener of this.listeners) {
      listener();
    }
  }
}

function areBindingsEqual(
  a: Record<string, ComponentStateScenario>,
  b: Record<string, ComponentStateScenario>
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    const av = a[key];
    const bv = b[key];
    if (!av || !bv) return false;
    if (av.id !== bv.id || av.label !== bv.label) return false;
    if (!areScenarioPropsEqual(av.props, bv.props)) return false;
  }
  return true;
}

function areScenarioPropsEqual(
  a: ComponentStateScenario['props'],
  b: ComponentStateScenario['props']
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  // Scenario props are plain JSON-serializable records (see
  // `normalizeProps`) and are delivered to the `<code-island>` host via
  // `JSON.stringify`, so a string compare here matches the exact bytes the
  // runtime reads. A key-order difference triggers a (harmless) extra
  // remount rather than a stale render — a false "equal" is the only
  // outcome we must avoid, since that is what leaves the preview stale.
  return JSON.stringify(a) === JSON.stringify(b);
}

export default CodeIslandCustomElementStore;
